import { useEffect, useState } from 'react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * OAuth callback landing page.
 *
 * Flow:
 *  1. Read platform + error from URL.
 *  2. If success, poll the backend a few times to give PostPeer a moment to
 *     register the new integration (spec: "integration not immediately visible
 *     → poll a few times before failing").
 *  3. Notify the opener tab via postMessage (Settings page listens).
 *  4. Auto-close after a short delay.
 */
export default function IntegrationsCallback() {
  const [status, setStatus] = useState('Verbindung wird hergestellt...');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const platform = params.get('platform');
    const error = params.get('error');

    const notifyOpener = (result) => {
      try {
        if (window.opener) {
          window.opener.postMessage(
            { type: 'OUTSTAND_CONNECTED', platform, ...result },
            window.location.origin
          );
        }
      } catch (_) { /* opener may be cross-origin — ignore */ }
    };

    const closeWindow = () => {
      try { window.close(); } catch (_) { /* ignored */ }
      setTimeout(() => {
        if (!window.closed) {
          window.location.href = '/app/einstellungen?tab=integrations';
        }
      }, 500);
    };

    if (error) {
      setStatus('Verbindung fehlgeschlagen. Du kannst dieses Fenster jetzt schließen.');
      notifyOpener({ status: 'error', error });
      setTimeout(closeWindow, 2000);
      return;
    }

    // Success path — poll for the new integration
    let cancelled = false;
    const pollForNewIntegration = async () => {
      const token = localStorage.getItem('token');
      if (!token || !platform) {
        setStatus('Verbunden! Fenster schließt sich...');
        notifyOpener({ status: 'ok' });
        setTimeout(closeWindow, 1000);
        return;
      }
      // Try up to 5 times × 1.5s = 7.5s max
      const attempts = 5;
      for (let i = 0; i < attempts; i++) {
        if (cancelled) return;
        try {
          const res = await fetch(`${API}/integrations/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const connected = Array.isArray(data.connected) ? data.connected : [];
            if (connected.includes(platform.toLowerCase())) {
              const label = platform.charAt(0).toUpperCase() + platform.slice(1);
              setStatus(`${label} verbunden! Dieses Fenster schließt sich gleich.`);
              notifyOpener({ status: 'ok', details: data.details?.[platform] || null });
              setTimeout(closeWindow, 1200);
              return;
            }
          }
        } catch (_) { /* transient — keep polling */ }
        setStatus(`Verbindung wird verifiziert... (${i + 1}/${attempts})`);
        await new Promise(r => setTimeout(r, 1500));
      }
      // Still not visible — notify success anyway (PostPeer sometimes lags),
      // opener will re-poll integrations on OUTSTAND_CONNECTED.
      setStatus('Verbindung angelegt. Falls sie nicht sofort erscheint, lade Einstellungen neu.');
      notifyOpener({ status: 'ok', delayed: true });
      setTimeout(closeWindow, 2500);
    };
    pollForNewIntegration();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      data-testid="integrations-callback-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#0A1628',
        color: 'white',
        fontFamily: 'sans-serif',
        gap: '16px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid #00C9A7',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}
      />
      <p style={{ fontSize: '16px', color: '#00C9A7' }} data-testid="integrations-callback-status">
        {status}
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
